import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdResource,
  BdResourceService,
} from '@bindoc-projects/resource-domain-lib';
import { BdLoaderComponent, BdMessageEmptyComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { ButtonDirective } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { ReplaySubject, take } from 'rxjs';
import { FeatureResourceRequest } from '../core/abstract-feature-resource.service';

export interface BdFeatureResourceSnippetLoadDialogData<
  TContent,
  TResource extends BdResource,
> {
  service: BdResourceService<TResource, FeatureResourceRequest<TContent>>;
}

@Component({
  selector: 'bd-feature-resource-snippet-load-dialog',
  template: `
    <div class="flex flex-col gap-4">
      <bd-loader
        [loadItem]="loading$"
        [customLoadingTemplate]="loadingTpl"
        [successTemplate]="successTpl"
      >
      </bd-loader>

      <div class="flex flex-row justify-end">
        <button pButton text severity="secondary" (click)="ref.close(null)">
          Abbrechen
        </button>
      </div>
    </div>

    <ng-template #loadingTpl>
      <div class="flex flex-col gap-2">
        @for (_ of [1, 2, 3]; track $index) {
          <div class="flex flex-row gap-2">
            <div class="w-1/4">
              <p-skeleton width="100%" height="36px"></p-skeleton>
            </div>
            <div class="w-1/4">
              <p-skeleton width="100%" height="36px"></p-skeleton>
            </div>
            <div class="w-1/4">
              <p-skeleton width="100%" height="36px"></p-skeleton>
            </div>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #successTpl>
      @if (resources().length === 0) {
        <bd-message-empty
          icon="pi pi-folder-open"
          message="Keine gespeicherten Analysen vorhanden."
        ></bd-message-empty>
      } @else {
        <p-table
          [value]="resources()"
          [paginator]="resources().length > 10"
          [rows]="10"
          [rowsPerPageOptions]="[10, 25, 50]"
          [showPageLinks]="false"
          [showFirstLastIcon]="false"
          sortField="modifiedDate"
          [sortOrder]="-1"
          dataKey="id"
          class="p-datatable-sm"
        >
          <ng-template #header>
            <tr>
              <th pSortableColumn="name">
                Name
                <p-sort-icon field="name"></p-sort-icon>
              </th>
              <th pSortableColumn="modifiedDate" style="width: 180px">
                Letzte Änderung
                <p-sort-icon field="modifiedDate"></p-sort-icon>
              </th>
              <th style="width: 80px"></th>
            </tr>
          </ng-template>
          <ng-template #body let-resource>
            <tr>
              <td>{{ resource.name }}</td>
              <td>{{ resource.modifiedDate | date: 'short' }}</td>
              <td>
                <button
                  pButton
                  text
                  data-test="feature-resource-snippet-load-dialog-load-button"
                  (click)="ref.close(resource)"
                  iconOnly
                >
                  <i class="pi pi-folder-open" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ng-template>
  `,
  imports: [
    BdLoaderComponent,
    BdMessageEmptyComponent,
    ButtonDirective,
    DatePipe,
    SkeletonModule,
    TableModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdFeatureResourceSnippetLoadDialogComponent<
  TContent,
  TResource extends BdResource,
> implements OnInit {
  readonly loading$ = new ReplaySubject<BdLoaderState>(1);
  readonly resources = signal<TResource[]>([]);

  private readonly _config: DynamicDialogConfig<
    BdFeatureResourceSnippetLoadDialogData<TContent, TResource>
  > = inject(DynamicDialogConfig);
  private readonly _destroyRef = inject(DestroyRef);
  readonly ref: DynamicDialogRef = inject(DynamicDialogRef);

  ngOnInit(): void {
    this.loading$.next(BdLoaderState.LOADING);
    this._config.data.service
      .findAll()
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (resources) => {
          this.resources.set(resources as TResource[]);
          this.loading$.next(BdLoaderState.SUCCESS);
        },
        error: () => this.loading$.next(BdLoaderState.ERROR),
      });
  }
}
